import AttachFileIcon from '@mui/icons-material/AttachFile'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ClearIcon from '@mui/icons-material/Clear'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import EditNoteIcon from '@mui/icons-material/EditNote'
import FileDownloadDoneIcon from '@mui/icons-material/FileDownloadDone'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { Badge, Box, Checkbox, Chip, Divider, IconButton, Tooltip, Typography } from '@mui/material'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import CustomBackdrop from 'common/components/CustomBackdrop'
import { CustomModalContext } from 'common/contexts/CustomModalContext'
import { ModalTypes } from 'common/hooks/useCustomModal'
import { DocIcon } from 'icons/DocIcon'
import i18n from 'lang/idiomas'
import { useContext, useState } from 'react'
import { GlobalVariablesContext } from 'src/common/providers/GlobalVariablesProvider'
import DocumentViewer from 'src/features/documentos/components/DocumentViewer'
import FilesHelper from 'src/common/libs/Files'
import {
  enrichFileForSigning,
  getActionableFile,
  getAdjuntosCount,
  getNumeroExpediente,
} from './Constants'
import DocumentDetailDialog from './DocumentDetailDialog'

const isDocFirmable = (doc) =>
  doc.permitir_adjuntar_documentos === 'N' &&
  (doc.ficheros_a_firmar ?? []).some((f) => f.puede_firmar === 'S')

const TarjetaDocumento = ({
  item,
  setTrigger,
  selectedDocs,
  setSelectedDocs,
  setActiveDoc,
  setRowAction,
  setRowModalOpen,
  statusColorMap,
  tipoDocColorMap,
}) => {
  const { handleModal } = useContext(CustomModalContext)
  const { IS_GOTTENBERG_ACTIVE } = useContext(GlobalVariablesContext)
  const [detailDoc, setDetailDoc] = useState(null)
  const [openBackdrop, setOpenBackdrop] = useState(false)

  const verDocumento = async (fichero) => {
    handleModal({
      enabled: true,
      closable: true,
      type: ModalTypes.Custom,
      content: (
        <DocumentViewer
          document_url={`/api/v1/documentos/descargaFichero/${fichero.token_acceso}/`}
          forced={false}
          title={fichero.nombre_archivo}
        />
      ),
    })
  }

  const descargar = async (fichero) => {
    setOpenBackdrop(true)
    await FilesHelper.downloadFile(
      null,
      null,
      `/api/v1/documentos/descargaFichero/${fichero.token_acceso}/`,
    ).finally(() => {
      setOpenBackdrop(false)
    })
  }

  const openRowDialog = (fichero, action) => {
    setActiveDoc(fichero)
    setRowAction(action)
    setRowModalOpen(true)
  }

  const handleFileAction = (enrichedFile, action) => {
    setActiveDoc(enrichedFile)
    setRowAction(action)
    setRowModalOpen(true)
  }

  const handleDocumentSelect = (checked) => {
    const next = new Set(selectedDocs)
    if (checked) {
      next.add(item.docId)
    } else {
      next.delete(item.docId)
    }
    setSelectedDocs(next)
  }

  const docColor = tipoDocColorMap?.[item.codigo_tipo_doc]

  const firstDate = item.ficheros_a_firmar?.[0]?.fecha_documento
  const adjuntosCount = getAdjuntosCount(item)
  const numeroExpediente = getNumeroExpediente(item)
  const isSelected = selectedDocs.has(item.docId)
  const actionFile = getActionableFile(item)

  return (
    <>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          border: isSelected ? '2px solid' : '1px solid',
          borderColor: isSelected ? 'primary.main' : 'divider',
          transition: 'border-color 0.2s',
          '&:hover': { boxShadow: 4 },
        }}
        onClick={() => setDetailDoc(item)}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            pt: 1.5,
            pb: 0.5,
          }}
        >
          <DocIcon
            kind={item.codigo_tipo_doc?.toLowerCase()}
            color={docColor}
            size={36}
            decorative
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {numeroExpediente && (
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                {i18n.t('exped', { defaultValue: 'Expediente:' })} {numeroExpediente}
              </Typography>
            )}
            <Tooltip title={item.descripcion?.length > 40 ? item.descripcion : ''} arrow>
              <Typography variant="h6" sx={{ lineHeight: 1.3, fontSize: '1.1rem' }}>
                {item.descripcion?.length > 40
                  ? item.descripcion.substring(0, 40) + '...'
                  : item.descripcion}
              </Typography>
            </Tooltip>
          </Box>
          {adjuntosCount > 0 && (
            <Badge badgeContent={adjuntosCount} color="secondary" sx={{ mr: 1 }}>
              <AttachFileIcon fontSize="small" color="action" />
            </Badge>
          )}
        </Box>

        <Divider sx={{ mx: 1.5, my: 2 }}>
          <Chip
            label={statusColorMap?.[`${item.codigo_tipo_doc}-${item.codigo_status}`]?.label ?? item.codigo_status}
            size="small"
            sx={
              statusColorMap?.[`${item.codigo_tipo_doc}-${item.codigo_status}`]
                ? {
                    bgcolor:
                      statusColorMap[`${item.codigo_tipo_doc}-${item.codigo_status}`].bgcolor,
                    color: statusColorMap[`${item.codigo_tipo_doc}-${item.codigo_status}`].color,
                  }
                : undefined
            }
          />
        </Divider>

        <CardContent sx={{ flexGrow: 1, pt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <CalendarTodayIcon fontSize="small" color="action" />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {firstDate
                ? new Date(firstDate).toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })
                : ''}
            </Typography>
          </Box>
        </CardContent>

        <CardActions disableSpacing sx={{ pt: 0 }} onClick={(e) => e.stopPropagation()}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.25, width: '100%' }}>
            {actionFile && (
              <>
                {(IS_GOTTENBERG_ACTIVE || actionFile.mime_type === 'application/pdf') && (
                  <Tooltip title="Ver" placement="top">
                    <IconButton
                      size="medium"
                      onClick={() => verDocumento(actionFile)}
                    >
                      <VisibilityIcon fontSize="medium" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Descargar" placement="top">
                  <IconButton size="medium" onClick={() => descargar(actionFile)}>
                    <CloudDownloadIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>
                {actionFile.puede_firmar === 'S' && (
                  <>
                    <Tooltip title="Observaciones" placement="top">
                      <IconButton
                        size="medium"
                        onClick={() => openRowDialog(enrichFileForSigning(actionFile, item), 'O')}
                      >
                        <EditNoteIcon fontSize="medium" sx={{ color: '#426bba' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Firmar" placement="top">
                      <IconButton
                        size="medium"
                        onClick={() => openRowDialog(enrichFileForSigning(actionFile, item), 'F')}
                      >
                        <FileDownloadDoneIcon fontSize="medium" sx={{ color: '#7FB77E' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Rechazar" placement="top">
                      <IconButton
                        size="medium"
                        onClick={() => openRowDialog(enrichFileForSigning(actionFile, item), 'R')}
                      >
                        <ClearIcon fontSize="medium" sx={{ color: '#E64848' }} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </>
            )}
            {isDocFirmable(item) && (
              <Tooltip title="Seleccionar para firma" placement="top">
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => handleDocumentSelect(e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ ml: 'auto', flexShrink: 0, '& .MuiSvgIcon-root': { fontSize: 28 } }}
                />
              </Tooltip>
            )}
          </Box>
        </CardActions>
      </Card>

      <CustomBackdrop
        open={openBackdrop}
        text={i18n.t('descargandoArchivo', { defaultValue: 'Descargando archivo' })}
      />

      {/* Detail Dialog */}
      <DocumentDetailDialog
        document={detailDoc}
        open={Boolean(detailDoc)}
        onClose={() => setDetailDoc(null)}
        onFileAction={handleFileAction}
        setTrigger={setTrigger}
        statusColorMap={statusColorMap}
        tipoDocColorMap={tipoDocColorMap}
      />
    </>
  )
}

export default TarjetaDocumento
